import React, { useState, useRef } from "react";
import { ReportAPI } from "../../api/apiClient";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface Props {
  projectId: number;
  projectName: string;
}

interface DrawingRow {
  drsNotesNo: number;
  note: string;
  remarks: string;
  by: string;
  drsDate: string;
}

export default function DrawingsReport({ projectId, projectName }: Props) {

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rows, setRows] = useState<DrawingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const reportRef = useRef<HTMLDivElement>(null);

  const loadReport = async () => {

    if (!startDate || !endDate) {
      alert("Select date range");
      return;
    }

    try {

      setLoading(true);

      const data = await ReportAPI.getDrawingsReport({
        projectId,
        startDate,
        endDate
      });

      setRows(data);
      setShowReport(true);

    } catch {
      alert("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  /* DATE FORMAT */

  const formatDate = (date: string) => {

    if (!date) return "";

    const d = new Date(date);

    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  /* EXPORT PDF */

  const exportPDF = async () => {

    if (!reportRef.current) return;

    const element = reportRef.current;

    const canvas = await html2canvas(element, {
      scale: window.devicePixelRatio * 2,
      useCORS: true,
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: element.scrollWidth,
      backgroundColor: "#ffffff"
    });

    const imgData = canvas.toDataURL("image/png", 1.0);

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: false
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const margin = 8; // increased content width
    const usableWidth = pdfWidth - margin * 2;

    const imgHeight = (canvas.height * usableWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(
      imgData,
      "PNG",
      margin,
      position,
      usableWidth,
      imgHeight,
      undefined,
      "NONE"
    );

    heightLeft -= pdfHeight - margin * 2;

    while (heightLeft > 0) {

      position = heightLeft - imgHeight + margin;

      pdf.addPage();

      pdf.addImage(
        imgData,
        "PNG",
        margin,
        position,
        usableWidth,
        imgHeight,
        undefined,
        "NONE"
      );

      heightLeft -= pdfHeight - margin * 2;
    }

    /* Page Numbers */

    const totalPages = pdf.getNumberOfPages();

    pdf.setFont("helvetica");
    pdf.setFontSize(11);

    for (let i = 1; i <= totalPages; i++) {

      pdf.setPage(i);

      pdf.text(
        `Page ${i} of ${totalPages}`,
        pdfWidth / 2,
        pdfHeight - 6,
        { align: "center" }
      );
    }

    pdf.save("DrawingsReport.pdf");
  };

  return (
    <div className="p-6">

      {/* FILTER PAGE */}

      {!showReport && (

        <div className="max-w-md">

          <h2 className="text-2xl font-semibold mb-6">
            Drawings Report
          </h2>

          <div className="mb-4">

            <label className="block mb-1 text-gray-700">
              From Date
            </label>

            <input
              type="date"
              value={startDate}
              className="w-full border px-3 py-2 rounded bg-white"
              onChange={(e) => setStartDate(e.target.value)}
            />

          </div>

          <div className="mb-6">

            <label className="block mb-1 text-gray-700">
              To Date
            </label>

            <input
              type="date"
              value={endDate}
              className="w-full border px-3 py-2 rounded bg-white"
              onChange={(e) => setEndDate(e.target.value)}
            />

          </div>

          <button
            onClick={loadReport}
            className="bg-green-600 text-white px-5 py-2 rounded hover:bg-green-700"
          >
            Run Report
          </button>

        </div>

      )}

      {/* REPORT PAGE */}

      {showReport && (

        <div className="w-full">

          {loading && <div>Loading...</div>}

          {!loading && (

            <>

              <div className="flex justify-end mb-3">

                <button
                  onClick={exportPDF}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  Export PDF
                </button>

              </div>

              {/* REPORT CONTENT */}

              <div ref={reportRef} className="w-full">

                <div className="text-3xl font-semibold mb-3">
                  Drawings Report
                </div>

                <div className="bg-slate-700 text-white px-4 py-3 font-medium text-lg">
                  {projectName}: Drawing Report from {formatDate(startDate)} to {formatDate(endDate)}
                </div>

                <div className="bg-slate-300 h-6"></div>

                {rows.length === 0 && (

                  <div className="px-3 py-4 text-gray-600 text-lg">
                    No data for this selected date range.
                  </div>

                )}

                {rows.map((r) => (

                  <div
                    key={r.drsNotesNo}
                    className="flex border-b border-gray-300 text-lg"
                  >

                    <div className="w-48 px-3 py-2">
                      {formatDate(r.drsDate)}
                    </div>

                    <div className="flex-1 px-3 py-2">
                      {r.note}
                    </div>

                  </div>

                ))}

              </div>

              <div className="mt-6">

                <button
                  onClick={() => setShowReport(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Back
                </button>

              </div>

            </>

          )}

        </div>

      )}

    </div>
  );
}